/**
 * Gider pusulası — müşteriden (fatura kesemeyen kişiden) yapılan alımlar.
 *
 * KURAL (kullanıcı, 01.10.2026): yalnız HAVALE/EFT ile ödeme yapılan alışlara
 * pusula kesilir — kısmi de olsa. Pusula tutarı işlemin tamamı değil, BANKADAN
 * ÖDENEN kısımdır; fatura tarafındaki mantığın aynısı (nakit ödeme belgelenmiyor).
 *
 * Örnek: 43.000 TL'lik alışın 25.300'ü havale ise pusula 25.300 TL olur.
 *
 * NOT: Kolaysoft'ta e-Gider Pusulası servisi var
 * (`ExpenseNoteService/ExpenseNoteWS`) ama hesabımız HENÜZ YETKİLİ DEĞİL.
 * Ayrıca stopaj / KDV biçimi mali müşavire soruldu. Bu yüzden şimdilik yalnız
 * aday listesi üretiliyor; belge üretimi ve gönderim cevaplar gelince eklenecek.
 */

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/** Alış işlemi mi (satış/giriş/çıkış değil)? */
export const isAlisIslemi = (islem) => String(islem?.islemTuru || '').trim() === 'Alış';

/** Bu alışa gider pusulası kesilmeli mi? */
export const giderPusulasiGerekir = (islem) =>
  isAlisIslemi(islem) && round2(islem?.havaleEft) > 0;

/** Pusulaya yazılacak tutar: bankadan ödenen kısım. */
export const giderPusulasiTutari = (islem) => round2(islem?.havaleEft);

/** Ödemenin tamamı havale mi, yoksa karma mı? */
export const odemeTipi = (islem) => {
  const havale = round2(islem?.havaleEft);
  const nakit = round2(islem?.nakit);
  const kart = round2(islem?.krediKarti);
  if (havale > 0 && (nakit > 0 || kart > 0)) return 'karma';
  if (havale > 0) return 'havale';
  return 'havale-disi';
};

/**
 * Pusulada satıcının adresi boşsa yazılacak şube varsayılanı.
 * Kullanıcı kararı (02.10.2026): Kavaklı işlemlerinde Beylikdüzü, Şefaköy
 * şube ve merkez işlemlerinde Küçükçekmece.
 */
export const SUBE_VARSAYILAN_ADRES = {
  kavakli_sube: 'Beylikdüzü / İSTANBUL',
  sefakoy_sube: 'Küçükçekmece / İSTANBUL',
  sefakoy_merkez: 'Küçükçekmece / İSTANBUL'
};

export const subeVarsayilanAdres = (sube) => SUBE_VARSAYILAN_ADRES[String(sube || '').trim()] || '';

/** Müşteri adı (pusulada satıcı olarak yazılacak). */
export const pusulaSaticiAdi = (islem) => {
  const m = islem?.musteri;
  if (!m) return '';
  return [m.isim, m.soyisim].filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
};

/**
 * Pusula adaylarını üretir. Kesilmiş olanlar `kesilmisIslemIds` ile ayıklanır.
 *
 * @param {object[]} islemler
 * @param {Set<string>|string[]} kesilmisIslemIds
 * @returns {{ id, islemNo, tarih, satici, urun, kategori, gram, adet, islemTutari, pusulaTutari, odeme, musteriEksik }[]}
 */
export const giderPusulasiAdaylari = (islemler = [], kesilmisIslemIds = []) => {
  const kesilmis = kesilmisIslemIds instanceof Set ? kesilmisIslemIds : new Set((kesilmisIslemIds || []).map(String));
  return (islemler || [])
    .filter((i) => i?.id && giderPusulasiGerekir(i) && !kesilmis.has(String(i.id)))
    .map((i) => ({
      id: String(i.id),
      islemNo: i.islemNo || i.id,
      tarih: gunKey(i),
      satici: pusulaSaticiAdi(i),
      musteriId: i?.musteri?.id || '',
      urun: i.urun || i.urunAdi || '',
      kategori: i.kategori || '',
      gram: Number(i.gram) || 0,
      adet: Number(i.adet) || 0,
      hasFiyati: round2(i.hasFiyati),
      islemTutari: round2(i.toplamTutar),
      pusulaTutari: giderPusulasiTutari(i),
      odeme: odemeTipi(i),
      musteriEksik: !pusulaSaticiAdi(i)
    }))
    .sort((a, b) => String(b.tarih).localeCompare(String(a.tarih)) || String(b.islemNo).localeCompare(String(a.islemNo)));
};

/** İşlemin İstanbul takvim günü (YYYY-MM-DD). */
export const gunKey = (islem) => {
  const v = islem?.timestamp || islem?.tarih || islem?.createdAt;
  if (!v) return '';
  const d = typeof v?.toDate === 'function' ? v.toDate() : typeof v?.seconds === 'number' ? new Date(v.seconds * 1000) : new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-CA', { timeZone: 'Europe/Istanbul' });
};

/** Dönem (tek ay ya da ay listesi) süzgeci. */
export const donemeGoreSuz = (adaylar = [], donem) => {
  const aylar = Array.isArray(donem) ? donem.filter(Boolean) : donem ? [donem] : [];
  if (!aylar.length) return adaylar;
  const kume = new Set(aylar);
  return adaylar.filter((a) => kume.has(String(a.tarih).slice(0, 7)));
};

/** Liste özeti. */
export const adayOzeti = (adaylar = []) => ({
  adet: adaylar.length,
  toplam: round2(adaylar.reduce((t, a) => t + a.pusulaTutari, 0)),
  karma: adaylar.filter((a) => a.odeme === 'karma').length,
  musteriEksik: adaylar.filter((a) => a.musteriEksik).length
});
